<template>
  <button 
    class="mui-toggle-btn" :class="[
    size ? `mui-toggle-btn--${size}` : '',
    color ? `mui-toggle-btn--${color}` : '',
    {
      'mui-toggle-btn--selected': selected,
      'mui-toggle-btn--disabled': disabled,
      'mui-toggle-btn--full-width': fullWidth
    }
  ]" :disabled="disabled" :aria-pressed="selected.toString()" :aria-label="ariaLabel" @click="handleClick"
    v-bind="$attrs">
    <!-- Left Icon Slot -->
    <span v-if="$slots.icon || icon" class="mui-toggle-btn__icon">
      <slot name="icon">{{ icon }}</slot>
    </span>

    <!-- Label -->
    <span v-if="label || $slots.default" class="mui-toggle-btn__label">
      <slot>{{ label }}</slot><br/>
      <span style="font-weight: bold;color: #1e8823e8">{{ extra }}</span>
    </span>

    <!-- Right Icon Slot -->
    <span v-if="$slots['icon-right']" class="mui-toggle-btn__icon mui-toggle-btn__icon--right">
      <slot name="icon-right" />
    </span>

    <!-- Ripple Effect (Material Design) -->
    <!-- <span v-if="ripple && !disabled" class="mui-toggle-btn__ripple"
      :class="{ 'mui-toggle-btn__ripple--active': rippleActive }"></span> -->
  </button>
</template>

<script>
import { Ripple } from '../../directives';
export default {
  name: 'toggle-button',
  inheritAttrs: false,
  props: {
    // v-model binding for selected state
    selected: {
      type: Boolean,
      default: false
    },
    // Button label
    label: {
      type: String,
      default: ''
    },
    extra: {
      type: String,
      default: ''
    },
    // Icon (can be text/emoji or CSS class)
    icon: {
      type: String,
      default: ''
    },
    // Size: 'small', 'medium', 'large'
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    // Color theme
    color: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'default', 'error', 'info', 'success', 'warning'].includes(value)
    },
    // Disabled state
    disabled: {
      type: Boolean,
      default: false
    },
    // Full width button
    fullWidth: {
      type: Boolean,
      default: false
    },
    // Enable ripple effect
    ripple: {
      type: Boolean,
      default: true
    },
    // ARIA label for accessibility
    ariaLabel: {
      type: String,
      default: ''
    },
    // Value for group selection
    value: {
      type: [String, Number],
      default: null
    }
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      rippleActive: false,
      rippleTimeout: null
    };
  },
  methods: {
    handleClick(event) {

      console.log(event);

      if (this.disabled) return;

      // Trigger ripple effect
      if (this.ripple) {
        this.rippleActive = true;
        clearTimeout(this.rippleTimeout);
        this.rippleTimeout = setTimeout(() => {
          this.rippleActive = false;
        }, 600);
      }

      // Emit events
      const newSelected = !this.selected;
      this.$emit('click', this.value);
      this.$emit('update:selected', newSelected);
      this.$emit('change', newSelected);

      // If used in a group, emit value for group handling
      if (this.value !== null) {
        this.$emit('toggle', this.value);
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.rippleTimeout);
  }
};
</script>

<style scoped>
/* Base Styles */
.mui-toggle-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 64px;
  padding: 6px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: transparent;
  color: rgba(0, 0, 0, 0.87);
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  text-transform: uppercase;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  overflow: hidden;
  white-space: nowrap;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

/* Selected State */
.mui-toggle-btn--selected {
  background-color: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.12);
}

/* Hover State */
.mui-toggle-btn:hover:not(.mui-toggle-btn--disabled) {
  background-color: rgba(0, 0, 0, 0.04);
}

.mui-toggle-btn--selected:hover:not(.mui-toggle-btn--disabled) {
  background-color: rgba(0, 0, 0, 0.12);
}

/* Disabled State */
.mui-toggle-btn--disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: default;
}

/* Full Width */
.mui-toggle-btn--full-width {
  width: 100%;
}

/* Sizes */
.mui-toggle-btn--small {
  padding: 4px 8px;
  font-size: 0.8125rem;
  min-width: 48px;
}

.mui-toggle-btn--large {
  padding: 8px 16px;
  font-size: 0.9375rem;
  min-width: 80px;
}

/* Colors */
.mui-toggle-btn--primary.mui-toggle-btn--selected {
  background-color: rgba(33, 150, 243, 0.08);
  color: #1976d2;
  border-color: #1976d2;
}

.mui-toggle-btn--primary:hover:not(.mui-toggle-btn--disabled) {
  background-color: rgba(33, 150, 243, 0.04);
}

.mui-toggle-btn--primary.mui-toggle-btn--selected:hover:not(.mui-toggle-btn--disabled) {
  background-color: rgba(33, 150, 243, 0.12);
}

.mui-toggle-btn--secondary.mui-toggle-btn--selected {
  background-color: rgba(156, 39, 176, 0.08);
  color: #9c27b0;
  border-color: #9c27b0;
}

.mui-toggle-btn--error.mui-toggle-btn--selected {
  background-color: rgba(211, 47, 47, 0.08);
  color: #d32f2f;
  border-color: #d32f2f;
}

.mui-toggle-btn--info.mui-toggle-btn--selected {
  background-color: rgba(2, 136, 209, 0.08);
  color: #0288d1;
  border-color: #0288d1;
}

.mui-toggle-btn--success.mui-toggle-btn--selected {
  background-color: rgba(46, 125, 50, 0.08);
  color: #2e7d32;
  border-color: #2e7d32;
}

.mui-toggle-btn--warning.mui-toggle-btn--selected {
  background-color: rgba(237, 108, 2, 0.08);
  color: #ed6c02;
  border-color: #ed6c02;
}

/* Icon Styles */
.mui-toggle-btn__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  line-height: 1;
}

.mui-toggle-btn__icon--right {
  margin-left: auto;
}

/* Ripple Effect */
/* .mui-toggle-btn__ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background-color: currentColor;
  opacity: 0.3;
  pointer-events: none;
  transition: .3s cubic-bezier(.25,.8,.25,1),color 1ms;
} */

.mui-toggle-btn__ripple--active {
  width: 150px;
  height: 150px;
  opacity: 0;
}

/* Focus Visible State for Accessibility */
.mui-toggle-btn:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}
</style>


// WEBPACK FOOTER //
// ToggleButton.vue?72632190